/**
 * formatAddress.js — v222.4.8
 *
 * The doctors.practice_address column was migrated from TEXT to JSONB in
 * v222.0.0 so Stedi 837P claim assembly could read structured
 * { address1, address2, city, state, postalCode, phone }. The migration was
 * correct for RCM, but the display path was not — every consumer that
 * expected a flat street string started rendering the raw JSON object
 * ({"address1": "433 N Camden...", ...}).
 *
 * The API normalizes practice_address to a flat string on the way out as of
 * v222.4.8, but this utility exists as a defense-in-depth measure so the
 * patient app never renders a raw JSON blob even if:
 *   - A stale API response (from before v222.4.8 deploy) is in flight
 *   - A new appointment row was written before the API fix landed
 *   - Some other code path slips past the API serializer
 *
 * Accepts: null, '', flat string, dict, or JSON-string of a dict.
 * Returns: clean "address1, address2" (skipping empty parts) or "".
 */

export function formatAddress(value) {
  if (!value) return ''

  // String input — could be plain or JSON-stringified dict
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed)
        return formatAddress(parsed)
      } catch {
        // Not parseable — return as-is (defensive)
        return trimmed
      }
    }
    return trimmed
  }

  // Object input
  if (typeof value === 'object') {
    const a1 = (value.address1 || '').trim()
    const a2 = (value.address2 || '').trim()
    const parts = []
    if (a1) parts.push(a1)
    if (a2) parts.push(a2)
    return parts.join(', ')
  }

  // Anything else (number, bool) — coerce
  return String(value)
}

/**
 * Build a full one-line address from any combination of:
 *   - street (string or structured object — handled by formatAddress)
 *   - city, state, zip (flat strings)
 *
 * Skips empty parts and produces "{street}, {city}, {state} {zip}".
 */
export function formatFullAddress({ address, city, state, zip } = {}) {
  const street = formatAddress(address)
  const parts = []
  if (street) parts.push(street)
  if (city) parts.push(String(city).trim())
  const stateZip = [state, zip].filter(Boolean).map(s => String(s).trim()).join(' ')
  if (stateZip) parts.push(stateZip)
  return parts.join(', ')
}

export default formatAddress
